import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";

import { SIGNED_OUT, type SessionStatus, auth } from "@/lib/api";
import { SessionProvider } from "@/lib/session";
import Account from "@/routes/Account";
import AddVessel from "@/routes/AddVessel";
import ImportVessel from "@/routes/ImportVessel";
import EditReading from "@/routes/EditReading";
import Equipment from "@/routes/Equipment";
import Events from "@/routes/Events";
import Gallery from "@/routes/Gallery";
import History from "@/routes/History";
import Home from "@/routes/Home";
import Landing from "@/routes/Landing";
import LogDose from "@/routes/LogDose";
import LogReading from "@/routes/LogReading";
import Login from "@/routes/Login";
import Privacy from "@/routes/Privacy";
import Pump from "@/routes/Pump";
import ReadingDetail from "@/routes/ReadingDetail";
import Settings from "@/routes/Settings";
import Setup from "@/routes/Setup";
import Terms from "@/routes/Terms";
import VesselPicker from "@/routes/VesselPicker";
import VesselRoute from "@/routes/VesselRoute";
import RequireAdmin from "@/routes/admin/RequireAdmin";

// Recharts is ~400 KB of the bundle. The dashboard is the screen opened
// most often and doesn't use it, so charts load on demand instead.
const Charts = lazy(() => import("@/routes/Charts"));
// Print routes pull in the chart bundle, so they are lazy too.
const PrintRoutes = lazy(() => import("@/routes/print"));
// The admin console is its own chunk: one person uses it, from a desk.
const AdminRoutes = lazy(() => import("@/routes/admin"));
// The guide is a long read, loaded when somebody opens it.
const Guide = lazy(() => import("@/routes/guide/Guide"));
const TestingGuide = lazy(() => import("@/routes/guide/Testing"));

export default function App() {
  const [status, setStatus] = useState<SessionStatus | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await auth.me());
    } catch {
      setStatus(SIGNED_OUT);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Print routes are rendered headlessly by the PDF service, which carries an
  // internal token rather than a session cookie — so they sit outside the
  // sign-in gate entirely.
  if (window.location.pathname.includes("/print/")) {
    return (
      <Suspense fallback={null}>
        <PrintRoutes />
      </Suspense>
    );
  }

  if (status === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  return (
    <SessionProvider value={{ status, refresh }}>
      <Routes>
        {/* Public, signed in or not. Google's consent screen and anyone
            deciding whether to sign up read these. */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* The front door answers differently once you are signed in (below),
            so the page itself has an address of its own. */}
        <Route path="/home" element={<Landing />} />
        {/* The guide is public: nothing in it is private, and it helps
            someone decide to sign up. Lazy, because it is a lot of words. */}
        <Route
          path="/guide"
          element={
            <Suspense fallback={null}>
              <Guide />
            </Suspense>
          }
        />
        <Route
          path="/guide/testing"
          element={
            <Suspense fallback={null}>
              <TestingGuide />
            </Suspense>
          }
        />
        <Route path="/login" element={status.authenticated ? <Navigate to="/" replace /> : <Login />} />

        {/* The front door answers three different questions depending on who
            is asking: the landing page, the setup wizard, or the picker. */}
        <Route path="/" element={<Front status={status} onSetup={() => void refresh()} />} />

        <Route element={<RequireSession status={status} />}>
          {/* Adding one is not about any existing vessel, so it sits outside
              the /v/:vesselId tree. */}
          <Route path="/add" element={<AddVessel />} />
          <Route path="/import" element={<ImportVessel />} />
          <Route path="/account" element={<Account />} />
          <Route
            path="/admin/*"
            element={
              <RequireAdmin>
                <Suspense
                  fallback={<div className="p-6 text-sm text-(--color-ink-muted)">Loading…</div>}
                >
                  <AdminRoutes />
                </Suspense>
              </RequireAdmin>
            }
          />
          {/* Everything else lives under /v/:vesselId, so the address bar
              always says which body of water is on screen — which is what
              makes the back button, a bookmark and a shared link mean the
              same thing. */}
          <Route path="/v/:vesselId" element={<VesselRoute />}>
            <Route index element={<Home />} />
            <Route path="log" element={<LogReading />} />
            <Route path="log/chemicals" element={<LogDose />} />
            <Route
              path="charts"
              element={
                <Suspense
                  fallback={
                    <div className="p-6 text-sm text-(--color-ink-muted)">Loading charts…</div>
                  }
                >
                  <Charts />
                </Suspense>
              }
            />
            {/* The More screen became the More menu; old bookmarks land on the dashboard. */}
            <Route path="more" element={<MoreRedirect />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="events" element={<Events />} />
            <Route path="pump" element={<Pump />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="history" element={<History />} />
            <Route path="history/:id" element={<ReadingDetail />} />
            <Route path="history/:id/edit" element={<EditReading />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Route>
        {/* Anything else — including every bookmark from before vessels existed —
            goes back through the front door rather than 404ing. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  );
}

function MoreRedirect() {
  const { vesselId } = useParams<{ vesselId: string }>();
  return <Navigate to={`/v/${vesselId}`} replace />;
}

function Front({ status, onSetup }: { status: SessionStatus; onSetup: () => void }) {
  if (!status.authenticated) return <Landing />;
  if (!status.setup_complete) return <Setup onComplete={onSetup} />;
  return <VesselPicker />;
}

/**
 * Signed-out visitors are sent to the login page with the path they wanted,
 * so a bookmarked dashboard still lands on the dashboard after sign-in. A
 * household with no vessel yet goes to setup instead — there is nothing under
 * /v/ for it to see.
 */
function RequireSession({ status }: { status: SessionStatus }) {
  const location = useLocation();
  if (!status.authenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!status.setup_complete) return <Navigate to="/" replace />;
  return <Outlet />;
}
